/* @jsx h */
import { defineComponent, ref, h } from "vue";
// @ts-ignore
import { qrcode } from "@lemmonhq/external/qrcode";

/**
 * Cell size for the rasterised form, in source pixels per module.
 *
 * A type-6 code is 41 modules across, so 3 gives a ~147px image. Small codes
 * are drawn at around 48 CSS px, which is 144 device pixels on a 3× phone —
 * near enough to one source pixel per device pixel that the modules stay square.
 */
const RASTER_CELL = 3;

export type QRCodeComponentProps = {
  url: string;
  /**
   * Draw the code as an image rather than inline SVG.
   *
   * The SVG form emits a path per dark module, which is several hundred nodes.
   * That is the right trade for a code someone scans off the screen, where it
   * fills a card and has to stay sharp at any size. It is the wrong one for a
   * small code repeated across a list or a stack: the nodes are the same either
   * way, they just get smaller. The rasterised form is a single element the
   * browser decodes once and composites as one texture.
   */
  raster?: boolean;
};

export const QRCodeComponent = defineComponent<QRCodeComponentProps>({
  name: "QRCodeComponent",
  props: ["url", "raster"],
  setup(props) {
    const qr = qrcode(6, "L");
    qr.addData(props.url);
    qr.make();
    // also qr.createDataURL is availble
    const code = ref(props.raster ? "" : qr.createSvgTag());
    const dataUrl = ref(props.raster ? qr.createDataURL(RASTER_CELL) : "");

    return () => {
      if (props.raster) {
        return (
          <img
            src={dataUrl.value}
            alt=""
            aria-hidden="true"
            class="w-full h-full"
            // Nearest-neighbour, or the browser's smoothing blurs the module
            // edges at the sizes this form is meant for and the code stops
            // scanning.
            style={{ imageRendering: "pixelated" }}
          />
        );
      }
      return (
        <div class="flex justify-center w-full">
          <div
            class="w-full h-full *:w-full *:h-full"
            v-html={code.value ?? ""}
          />
        </div>
      );
    };
  },
});
